import React, { useState } from 'react';
import { useAuth } from './AuthContext';
import FlameIcon from './FlameIcon';
import { INPUT, LABEL } from './ui';

// Full-screen "set a new password" form, styled like the Login screen. Used in
// two modes:
//  - forced:  rendered instead of the app while user.must_change_password is
//             set (first login / after a dev reset). No way out except logout.
//  - voluntary: opened from the account menu; offers Cancel and closes itself
//             after a successful change.
const ChangePassword = ({ forced = false, onClose }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const { changePassword, logout } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    const result = await changePassword(newPassword);
    setLoading(false);
    if (result.success) {
      // Forced mode: the updated user clears must_change_password and the app
      // gate lets them through automatically. Voluntary mode: show a brief
      // confirmation, then return to the app.
      if (!forced) {
        setSuccess(true);
        window.setTimeout(() => onClose(), 1200);
      }
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-800 via-gray-800 to-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-gray-800/50 backdrop-blur-sm border border-purple-500/20 rounded-2xl p-8 shadow-2xl">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <FlameIcon className="w-16 h-16" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">
              {forced ? 'Set your password' : 'Change password'}
            </h1>
            <p className="text-gray-400">
              {forced
                ? 'You signed in with a one-time password. Choose a permanent password to continue.'
                : 'Choose a new password for your account.'}
            </p>
          </div>

          {success ? (
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4 text-center">
              <p className="text-emerald-400 text-sm font-medium">Password updated.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="new-password" className={LABEL}>
                  New password
                </label>
                <input
                  id="new-password"
                  name="new-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  className={INPUT}
                />
              </div>

              <div>
                <label htmlFor="confirm-password" className={LABEL}>
                  Confirm new password
                </label>
                <input
                  id="confirm-password"
                  name="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter your new password"
                  className={INPUT}
                />
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-purple-500 to-violet-500 hover:from-purple-600 hover:to-violet-600 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {loading ? 'Saving...' : 'Set new password'}
              </button>

              {forced ? (
                <button
                  type="button"
                  onClick={logout}
                  className="w-full bg-transparent p-0 text-sm text-gray-400 hover:text-gray-200 transition-colors duration-200"
                >
                  Sign out
                </button>
              ) : (
                <button
                  type="button"
                  onClick={onClose}
                  className="w-full bg-transparent p-0 text-sm text-gray-400 hover:text-gray-200 transition-colors duration-200"
                >
                  Cancel
                </button>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
